import React, { useState, useEffect } from "react";
import {
  UtensilsCrossed,
  AlertCircle,
  Clock3,
  Droplets,
  Save,
  Coffee,
  Moon,
  Sun,
  Apple,
} from "lucide-react";
import HealthGoals from "./HealthGoals";
import { getProfileStepDetails, updateProfileStep } from "../../api/api";

const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`fixed top-5 right-5 z-50 flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg animate-slide-in ${type === "error" ? "bg-red-500" : type === "success" ? "bg-green-500" : "bg-blue-500"
      } text-white min-w-[300px]`}>
      <span className="text-sm flex-1">{message}</span>
      <button onClick={onClose} className="text-white hover:text-gray-200 text-xl">×</button>
    </div>
  );
};

const DietaryPreferences = () => {
  const [toasts, setToasts] = useState([]);
  const [activeScreen, setActiveScreen] = useState("dietaryPreferences");
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const [dietaryData, setDietaryData] = useState({
    foodHabits: "",
    foodCulture: [],
    foodCultureOther: "",
    snackingHabit: false,
    snacksDetails: "",
    eatOutsideFood: false,
    outsideFoodFrequency: "",
    teaConsumption: false,
    teaCupsPerDay: "",
    coffeeConsumption: false,
    coffeeCupsPerDay: "",
    softDrinksConsumption: false,
    softDrinksFrequency: "",
    sweetsConsumption: "",
    cookingOil: "",
    cookingOilOther: "",
    breakfastTime: "",
    breakfastMenu: "",
    lunchTime: "",
    lunchMenu: "",
    eveningSnackTime: "",
    eveningSnackMenu: "",
    dinnerTime: "",
    dinnerMenu: "",
    postDinnerConsumption: "",
    snackAfterDinner: false,
    afterDinnerSnacks: "",
    packagedFoodsConsumption: false,
    packagedFoodsDetails: "",
    weekendEatingPattern: false,
    weekendEatingDetails: "",
    foodSensitivities: false,       
    foodSensitivitiesDetails: "",    
    foodAllergies: false,            
    foodAllergiesDetails: ""
  });

  const [formErrors, setFormErrors] = useState({});
  const [showOtherFoodCulture, setShowOtherFoodCulture] = useState(false);
  const [showOtherCookingOil, setShowOtherCookingOil] = useState(false);

  const foodHabitsOptions = ["Vegetarian", "Non-Vegetarian", "Eggetarian", "Vegan", "Jain", "Can eat fish"];

  const foodCultureOptions = [
    "North Indian", "South Indian", "Gujrati", "Rajasthani", "Goa", "Maharashtra",
    "Punjab", "Bangoli", "Bihar", "Odisa", "Chinese", "Japanese", "Korean",
    "Thailand", "Mediterranean", "French", "Italian", "American", "African",
    "European", "Other"
  ];

  const cookingOilOptions = [
    "Mustard oil", "Groundnut oil", "Olive oil", "Ghee", "Sunflower oil",
    "Coconut oil", "Canola oil", "Soybean oil", "Rice bran oil", "Safflower oil",
    "Sesame oil", "Peanut oil", "Cottonseed oil", "Palm oil", "Avocado oil",
    "Flaxseed oil", "Walnut oil", "Pumpkin seed oil", "Chia oil", "Hemp seed oil", "Other"
  ];

  const frequencyOptions = ["Once a week", "2–3 times a week", "Daily", "Occasionally", "Never"];
  const sweetsOptions = ["Occasionally", "Often", "Daily"];

  const addToast = (message, type = "success") => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
  };

  const removeToast = (id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  useEffect(() => {
    fetchDietaryData();
  }, []);

  const fetchDietaryData = async () => {
    try {
      setLoading(true);
      const res = await getProfileStepDetails();

      if (res?.status) {
        const dietaryFields = res?.profile_completion?.steps?.dietary?.fields;

        const fieldMap = {};
        dietaryFields.forEach((item) => {
          fieldMap[item.name] = item.value;
        });

        console.log("Fetched Dietary Data:", fieldMap);

        let foodCultureArray = [];
        if (fieldMap.foodCulture) {
          if (typeof fieldMap.foodCulture === 'string') {
            foodCultureArray = fieldMap.foodCulture.split(',').map(s => s.trim());
          } else if (Array.isArray(fieldMap.foodCulture)) {
            foodCultureArray = fieldMap.foodCulture;
          }
        }

        setDietaryData({
          foodHabits: fieldMap.foodHabits || "",
          foodCulture: foodCultureArray,
          foodCultureOther: "",
          snackingHabit: fieldMap.snackingHabit || false,
          snacksDetails: fieldMap.snacksDetails || "",
          eatOutsideFood: fieldMap.eatOutsideFood || false,
          outsideFoodFrequency: fieldMap.outsideFoodFrequency || "",
          teaConsumption: fieldMap.teaConsumption || false,
          teaCupsPerDay: fieldMap.teaCupsPerDay || "",
          coffeeConsumption: fieldMap.coffeeConsumption || false,
          coffeeCupsPerDay: fieldMap.coffeeCupsPerDay || "",
          softDrinksConsumption: fieldMap.softDrinksConsumption || false,
          softDrinksFrequency: fieldMap.softDrinksFrequency || "",
          sweetsConsumption: fieldMap.sweetsConsumption || "",
          cookingOil: fieldMap.cookingOil || "",
          cookingOilOther: "",
          breakfastTime: fieldMap.breakfastTime || "",
          breakfastMenu: fieldMap.breakfastMenu || "",
          lunchTime: fieldMap.lunchTime || "",
          lunchMenu: fieldMap.lunchMenu || "",
          eveningSnackTime: fieldMap.eveningSnackTime || "",
          eveningSnackMenu: fieldMap.eveningSnackMenu || "",
          dinnerTime: fieldMap.dinnerTime || "",
          dinnerMenu: fieldMap.dinnerMenu || "",
          postDinnerConsumption: fieldMap.postDinnerConsumption || "",
          snackAfterDinner: fieldMap.snackAfterDinner || false,
          afterDinnerSnacks: fieldMap.afterDinnerSnacks || "",
          packagedFoodsConsumption: fieldMap.packagedFoodsConsumption || false,
          packagedFoodsDetails: fieldMap.packagedFoodsDetails || "",
          weekendEatingPattern: fieldMap.weekendEatingPattern || false,
          weekendEatingDetails: fieldMap.weekendEatingDetails || "",
          foodSensitivities: fieldMap.foodSensitivities || false,
          foodSensitivitiesDetails: fieldMap.foodSensitivitiesDetails || "",
          foodAllergies: fieldMap.foodAllergies || false,
          foodAllergiesDetails: fieldMap.foodAllergiesDetails || ""
        });
      }
    } catch (error) {
      console.error("Error fetching dietary data:", error);
      addToast("Failed to load dietary data", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setDietaryData(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  const handleFoodCultureToggle = (option) => {
    setDietaryData(prev => {
      const current = [...prev.foodCulture];
      if (current.includes(option)) {
        return { ...prev, foodCulture: current.filter(item => item !== option) };
      } else {
        return { ...prev, foodCulture: [...current, option] };
      }
    });
  };

  const validateForm = () => {
    const errors = {};

    if (!dietaryData.foodHabits) errors.foodHabits = "Please select food habits";
    if (dietaryData.foodCulture.length === 0) errors.foodCulture = "Please select at least one food culture";

    if (dietaryData.snackingHabit && !dietaryData.snacksDetails) {
      errors.snacksDetails = "Please specify what you eat";
    }

    if (dietaryData.eatOutsideFood && !dietaryData.outsideFoodFrequency) {
      errors.outsideFoodFrequency = "Please select frequency";
    }

    if (dietaryData.teaConsumption && !dietaryData.teaCupsPerDay) {
      errors.teaCupsPerDay = "Please enter number of cups";
    }

    if (dietaryData.coffeeConsumption && !dietaryData.coffeeCupsPerDay) {
      errors.coffeeCupsPerDay = "Please enter number of cups";
    }

    if (dietaryData.softDrinksConsumption && !dietaryData.softDrinksFrequency) {
      errors.softDrinksFrequency = "Please select frequency";
    }

    if (!dietaryData.sweetsConsumption) errors.sweetsConsumption = "Please select sweets consumption";
    if (!dietaryData.cookingOil) errors.cookingOil = "Please select cooking oil";

    if (dietaryData.snackAfterDinner && !dietaryData.afterDinnerSnacks) {
      errors.afterDinnerSnacks = "Please specify what you consume";
    }

    if (dietaryData.packagedFoodsConsumption && !dietaryData.packagedFoodsDetails) {
      errors.packagedFoodsDetails = "Please specify what you eat";
    }

    if (dietaryData.weekendEatingPattern && !dietaryData.weekendEatingDetails) {
      errors.weekendEatingDetails = "Please specify how it's different";
    }

    if (dietaryData.foodSensitivities && !dietaryData.foodSensitivitiesDetails) {
      errors.foodSensitivitiesDetails = "Please mention food sensitivities";
    }
    if (dietaryData.foodAllergies && !dietaryData.foodAllergiesDetails) {
      errors.foodAllergiesDetails = "Please mention food allergies";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveChanges = async () => {
    if (validateForm()) {
      setIsSaving(true);

      try {
        const dietaryPayload = {
          foodHabits: dietaryData.foodHabits || null,
          foodCulture: dietaryData.foodCulture.length > 0 ? dietaryData.foodCulture.join(", ") : null,
          snackingHabit: dietaryData.snackingHabit || false,
          snacksDetails: dietaryData.snacksDetails || null,
          eatOutsideFood: dietaryData.eatOutsideFood || false,
          outsideFoodFrequency: dietaryData.outsideFoodFrequency || null,
          teaConsumption: dietaryData.teaConsumption || false,
          teaCupsPerDay: dietaryData.teaCupsPerDay ? parseInt(dietaryData.teaCupsPerDay) : null,
          coffeeConsumption: dietaryData.coffeeConsumption || false,
          coffeeCupsPerDay: dietaryData.coffeeCupsPerDay ? parseInt(dietaryData.coffeeCupsPerDay) : null,
          softDrinksConsumption: dietaryData.softDrinksConsumption || false,
          softDrinksFrequency: dietaryData.softDrinksFrequency || null,
          sweetsConsumption: dietaryData.sweetsConsumption || null,
          cookingOil: dietaryData.cookingOil || null,
          breakfastTime: dietaryData.breakfastTime || null,
          breakfastMenu: dietaryData.breakfastMenu || null,
          lunchTime: dietaryData.lunchTime || null,
          lunchMenu: dietaryData.lunchMenu || null,
          eveningSnackTime: dietaryData.eveningSnackTime || null,
          eveningSnackMenu: dietaryData.eveningSnackMenu || null,
          dinnerTime: dietaryData.dinnerTime || null,
          dinnerMenu: dietaryData.dinnerMenu || null,
          postDinnerConsumption: dietaryData.postDinnerConsumption || null,
          snackAfterDinner: dietaryData.snackAfterDinner || false,
          afterDinnerSnacks: dietaryData.afterDinnerSnacks || null,
          packagedFoodsConsumption: dietaryData.packagedFoodsConsumption || false,
          packagedFoodsDetails: dietaryData.packagedFoodsDetails || null,
          weekendEatingPattern: dietaryData.weekendEatingPattern || false,
          weekendEatingDetails: dietaryData.weekendEatingDetails || null,
          foodSensitivities: dietaryData.foodSensitivities || false,
          foodSensitivitiesDetails: dietaryData.foodSensitivitiesDetails || null,
          foodAllergies: dietaryData.foodAllergies || false,
          foodAllergiesDetails: dietaryData.foodAllergiesDetails || null
        };

        const response = await updateProfileStep("dietary", dietaryPayload);

        if (response?.status) {
          addToast("Dietary preferences saved successfully!", "success");
          await fetchDietaryData();

          setTimeout(() => {
            setActiveScreen("healthGoals");
          }, 1500);
        } else {
          addToast(response?.message || "Failed to save data", "error");
        }
      } catch (error) {
        console.error("Save error:", error);
        addToast(error.message || "Failed to save dietary preferences", "error");
      } finally {
        setIsSaving(false);
      }
    } else {
      addToast("Please fill in all required fields", "error");
      const firstErrorField = Object.keys(formErrors)[0];
      if (firstErrorField) {
        const element = document.querySelector(`[name="${firstErrorField}"]`);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
          element.focus();
        }
      }
    }
  };

  const handleDiscard = () => {
    fetchDietaryData();
    setFormErrors({});
    addToast("All changes discarded", "info");
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#57b26a]"></div>
      </div>
    );
  }

  if (activeScreen === "healthGoals") {
    return <HealthGoals />;
  }

  return (
    <div>
      {toasts.map(toast => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          onClose={() => removeToast(toast.id)}
        />
      ))}

      <style jsx>{`
        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        .animate-slide-in {
          animation: slideIn 0.3s ease-out;
        }
      `}</style>

      <div className="mb-6">
        <h2 className="text-2xl sm:text-3xl font-semibold text-[#24312d] mb-2">
          Dietary Preferences
        </h2>
        <p className="text-[#98a19b] text-sm">
          Tailor your nutrition plan by updating your dietary restrictions and preferences.
        </p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="space-y-6">
          <div className="bg-white border border-[#e8ece8] rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <UtensilsCrossed size={18} className="text-green-500" />
              <h3 className="text-[16px] font-semibold text-[#24312d]">
                Food Habits & Culture
              </h3>
            </div>

            <div className="mb-4">
              <label className="block text-[13px] text-[#24312d] mb-2">
                Food Habits <span className="text-red-500">*</span>
              </label>
              <select
                name="foodHabits"
                value={dietaryData.foodHabits}
                onChange={(e) => handleInputChange("foodHabits", e.target.value)}
                className={`w-full h-[42px] border rounded-md px-4 text-sm outline-none focus:ring-1 ${formErrors.foodHabits
                    ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                    : "border-[#e1e6e1] focus:border-[#57b26a] focus:ring-[#57b26a]"
                  }`}
              >
                <option value="">Select</option>
                {foodHabitsOptions.map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
              {formErrors.foodHabits && (
                <p className="text-red-500 text-xs mt-1">{formErrors.foodHabits}</p>
              )}
            </div>

            <div className="mb-4">
              <label className="block text-[13px] text-[#24312d] mb-2">
                Food Culture / Cuisine <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto p-2 border border-[#e1e6e1] rounded-md">
                {foodCultureOptions.map(opt => (
                  <label key={opt} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={dietaryData.foodCulture.includes(opt)}
                      onChange={() => handleFoodCultureToggle(opt)}
                      className="w-4 h-4 accent-green-500"
                    />
                    <span className="text-[13px] text-[#24312d]">{opt}</span>
                  </label>
                ))}
              </div>
              {formErrors.foodCulture && (
                <p className="text-red-500 text-xs mt-1">{formErrors.foodCulture}</p>
              )}
            </div>

            {dietaryData.foodCulture.includes("Other") && (
              <div>
                <label className="block text-[13px] text-[#24312d] mb-2">
                  Mention Other Cuisine
                </label>
                <input
                  type="text"
                  value={dietaryData.foodCultureOther}
                  onChange={(e) => handleInputChange("foodCultureOther", e.target.value)}
                  placeholder="Please specify"
                  className="w-full h-[42px] border border-[#e1e6e1] rounded-md px-4 text-sm outline-none focus:border-[#57b26a]"
                />
              </div>
            )}
          </div>
          <div className="bg-white border border-[#e8ece8] rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <Clock3 size={18} className="text-green-500" />
              <h3 className="text-[16px] font-semibold text-[#24312d]">
                Meal Times
              </h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-[13px] text-[#24312d] mb-2">
                  Breakfast Time
                </label>
                <input
                  type="time"
                  value={dietaryData.breakfastTime}
                  onChange={(e) => handleInputChange("breakfastTime", e.target.value)}
                  className="w-full h-[42px] border border-[#e1e6e1] rounded-md px-4 text-sm outline-none focus:border-[#57b26a]"
                />
              </div>
              <div>
                <label className="block text-[13px] text-[#24312d] mb-2">
                  Breakfast Menu
                </label>
                <textarea
                  value={dietaryData.breakfastMenu}
                  onChange={(e) => handleInputChange("breakfastMenu", e.target.value)}
                  rows="2"
                  placeholder="e.g., Poha with peas and peanuts + 1 cup tea"
                  className="w-full border border-[#e1e6e1] rounded-md px-4 py-2 text-sm outline-none focus:border-[#57b26a] resize-none"
                />
              </div>

              <div>
                <label className="block text-[13px] text-[#24312d] mb-2">
                  Lunch Time
                </label>
                <input
                  type="time"
                  value={dietaryData.lunchTime}
                  onChange={(e) => handleInputChange("lunchTime", e.target.value)}
                  className="w-full h-[42px] border border-[#e1e6e1] rounded-md px-4 text-sm outline-none focus:border-[#57b26a]"
                />
              </div>
              <div>
                <label className="block text-[13px] text-[#24312d] mb-2">
                  Lunch Menu
                </label>
                <textarea
                  value={dietaryData.lunchMenu}
                  onChange={(e) => handleInputChange("lunchMenu", e.target.value)}
                  rows="2"
                  placeholder="e.g., 2 rotis + dal + sabzi + salad"
                  className="w-full border border-[#e1e6e1] rounded-md px-4 py-2 text-sm outline-none focus:border-[#57b26a] resize-none"
                />
              </div>

              <div>
                <label className="block text-[13px] text-[#24312d] mb-2">
                  Evening Snack Time
                </label>
                <input
                  type="time"
                  value={dietaryData.eveningSnackTime}
                  onChange={(e) => handleInputChange("eveningSnackTime", e.target.value)}
                  className="w-full h-[42px] border border-[#e1e6e1] rounded-md px-4 text-sm outline-none focus:border-[#57b26a]"
                />
              </div>
              <div>
                <label className="block text-[13px] text-[#24312d] mb-2">
                  Snacks Menu
                </label>
                <textarea
                  value={dietaryData.eveningSnackMenu}
                  onChange={(e) => handleInputChange("eveningSnackMenu", e.target.value)}
                  rows="2"
                  placeholder="e.g., 1 apple + 4 almonds / green tea"
                  className="w-full border border-[#e1e6e1] rounded-md px-4 py-2 text-sm outline-none focus:border-[#57b26a] resize-none"
                />
              </div>

              <div>
                <label className="block text-[13px] text-[#24312d] mb-2">
                  Dinner Time
                </label>
                <input
                  type="time"
                  value={dietaryData.dinnerTime}
                  onChange={(e) => handleInputChange("dinnerTime", e.target.value)}
                  className="w-full h-[42px] border border-[#e1e6e1] rounded-md px-4 text-sm outline-none focus:border-[#57b26a]"
                />
              </div>
              <div>
                <label className="block text-[13px] text-[#24312d] mb-2">
                  Dinner Menu
                </label>
                <textarea
                  value={dietaryData.dinnerMenu}
                  onChange={(e) => handleInputChange("dinnerMenu", e.target.value)}
                  rows="2"
                  placeholder="e.g., 1 bowl soup + 1 roti + paneer curry"
                  className="w-full border border-[#e1e6e1] rounded-md px-4 py-2 text-sm outline-none focus:border-[#57b26a] resize-none"
                />
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-[13px] text-[#24312d] mb-2">
                Post-Dinner Drink / Dessert
              </label>
              <input
                type="text"
                value={dietaryData.postDinnerConsumption}
                onChange={(e) => handleInputChange("postDinnerConsumption", e.target.value)}
                placeholder="e.g., 1 cup chamomile tea / warm milk"
                className="w-full h-[42px] border border-[#e1e6e1] rounded-md px-4 text-sm outline-none focus:border-[#57b26a]"
              />
            </div>

            <div className="mt-4">
              <div className="flex items-center justify-between mb-2">
                <label className="text-[13px] text-[#24312d]">
                  Do you snack after dinner?
                </label>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={dietaryData.snackAfterDinner}
                    onChange={(e) => handleInputChange("snackAfterDinner", e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#57b26a]"></div>
                  <span className="ml-3 text-sm text-[#24312d]">
                    {dietaryData.snackAfterDinner ? "Yes" : "No"}
                  </span>
                </label>
              </div>
              {dietaryData.snackAfterDinner && (
                <textarea
                  name="afterDinnerSnacks"
                  value={dietaryData.afterDinnerSnacks}
                  onChange={(e) => handleInputChange("afterDinnerSnacks", e.target.value)}
                  rows="2"
                  placeholder="What do you consume?"
                  className={`w-full border rounded-md px-4 py-2 text-sm outline-none focus:ring-1 mt-2 ${formErrors.afterDinnerSnacks
                      ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                      : "border-[#e1e6e1] focus:border-[#57b26a] focus:ring-[#57b26a]"
                    }`}
                />
              )}
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white border border-[#e8ece8] rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <Apple size={18} className="text-green-500" />
              <h3 className="text-[16px] font-semibold text-[#24312d]">
                Snacking & Eating Habits
              </h3>
            </div>

            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <label className="text-[13px] text-[#24312d]">
                  Do you have a habit of snacking between meals?
                </label>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={dietaryData.snackingHabit}
                    onChange={(e) => handleInputChange("snackingHabit", e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#57b26a]"></div>
                  <span className="ml-3 text-sm text-[#24312d]">
                    {dietaryData.snackingHabit ? "Yes" : "No"}
                  </span>
                </label>
              </div>
              {dietaryData.snackingHabit && (
                <textarea
                  name="snacksDetails"
                  value={dietaryData.snacksDetails}
                  onChange={(e) => handleInputChange("snacksDetails", e.target.value)}
                  rows="2"
                  placeholder="What do you eat? (e.g., Nuts, roasted chana, biscuits)"
                  className={`w-full border rounded-md px-4 py-2 text-sm outline-none focus:ring-1 mt-2 ${formErrors.snacksDetails
                      ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                      : "border-[#e1e6e1] focus:border-[#57b26a] focus:ring-[#57b26a]"
                    }`}
                />
              )}
            </div>

            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <label className="text-[13px] text-[#24312d]">
                  Do you eat outside food?
                </label>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={dietaryData.eatOutsideFood}
                    onChange={(e) => handleInputChange("eatOutsideFood", e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#57b26a]"></div>
                  <span className="ml-3 text-sm text-[#24312d]">
                    {dietaryData.eatOutsideFood ? "Yes" : "No"}
                  </span>
                </label>
              </div>
              {dietaryData.eatOutsideFood && (
                <select
                  name="outsideFoodFrequency"
                  value={dietaryData.outsideFoodFrequency}
                  onChange={(e) => handleInputChange("outsideFoodFrequency", e.target.value)}
                  className={`w-full h-[42px] border rounded-md px-4 text-sm outline-none focus:ring-1 mt-2 ${formErrors.outsideFoodFrequency
                      ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                      : "border-[#e1e6e1] focus:border-[#57b26a] focus:ring-[#57b26a]"
                    }`}
                >
                  <option value="">How often?</option>
                  {frequencyOptions.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              )}
            </div>
          </div>

          <div className="bg-white border border-[#e8ece8] rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <Coffee size={18} className="text-green-500" />
              <h3 className="text-[16px] font-semibold text-[#24312d]">
                Beverages
              </h3>
            </div>

            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <label className="text-[13px] text-[#24312d]">Do you drink Tea?</label>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={dietaryData.teaConsumption}
                    onChange={(e) => handleInputChange("teaConsumption", e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#57b26a]"></div>
                  <span className="ml-3 text-sm text-[#24312d]">
                    {dietaryData.teaConsumption ? "Yes" : "No"}
                  </span>
                </label>
              </div>
              {dietaryData.teaConsumption && (
                <input
                  type="text"
                  name="teaCupsPerDay"
                  value={dietaryData.teaCupsPerDay}
                  onChange={(e) => handleInputChange("teaCupsPerDay", e.target.value)}
                  placeholder="How many cups daily? (e.g., 3 cups/day)"
                  className={`w-full h-[42px] border rounded-md px-4 text-sm outline-none focus:ring-1 mt-2 ${formErrors.teaCupsPerDay
                      ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                      : "border-[#e1e6e1] focus:border-[#57b26a] focus:ring-[#57b26a]"
                    }`}
                />
              )}
            </div>

            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <label className="text-[13px] text-[#24312d]">Do you drink Coffee?</label>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={dietaryData.coffeeConsumption}
                    onChange={(e) => handleInputChange("coffeeConsumption", e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#57b26a]"></div>
                  <span className="ml-3 text-sm text-[#24312d]">
                    {dietaryData.coffeeConsumption ? "Yes" : "No"}
                  </span>
                </label>
              </div>
              {dietaryData.coffeeConsumption && (
                <input
                  type="text"
                  name="coffeeCupsPerDay"
                  value={dietaryData.coffeeCupsPerDay}
                  onChange={(e) => handleInputChange("coffeeCupsPerDay", e.target.value)}
                  placeholder="How many cups daily?"
                  className={`w-full h-[42px] border rounded-md px-4 text-sm outline-none focus:ring-1 mt-2 ${formErrors.coffeeCupsPerDay
                      ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                      : "border-[#e1e6e1] focus:border-[#57b26a] focus:ring-[#57b26a]"
                    }`}
                />
              )}
            </div>

            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <label className="text-[13px] text-[#24312d]">Do you drink Soft (Aerated) Drinks?</label>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={dietaryData.softDrinksConsumption}
                    onChange={(e) => handleInputChange("softDrinksConsumption", e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#57b26a]"></div>
                  <span className="ml-3 text-sm text-[#24312d]">
                    {dietaryData.softDrinksConsumption ? "Yes" : "No"}
                  </span>
                </label>
              </div>
              {dietaryData.softDrinksConsumption && (
                <select
                  name="softDrinksFrequency"
                  value={dietaryData.softDrinksFrequency}
                  onChange={(e) => handleInputChange("softDrinksFrequency", e.target.value)}
                  className={`w-full h-[42px] border rounded-md px-4 text-sm outline-none focus:ring-1 mt-2 ${formErrors.softDrinksFrequency
                      ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                      : "border-[#e1e6e1] focus:border-[#57b26a] focus:ring-[#57b26a]"
                    }`}
                >
                  <option value="">How frequently?</option>
                  {frequencyOptions.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              )}
            </div>
          </div>

          <div className="bg-white border border-[#e8ece8] rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <Sun size={18} className="text-green-500" />
              <h3 className="text-[16px] font-semibold text-[#24312d]">
                Cooking & Sweets
              </h3>
            </div>

            <div className="mb-4">
              <label className="block text-[13px] text-[#24312d] mb-2">
                Do you enjoy sweets / sugary food? <span className="text-red-500">*</span>
              </label>
              <select
                name="sweetsConsumption"
                value={dietaryData.sweetsConsumption}
                onChange={(e) => handleInputChange("sweetsConsumption", e.target.value)}
                className={`w-full h-[42px] border rounded-md px-4 text-sm outline-none focus:ring-1 ${formErrors.sweetsConsumption
                    ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                    : "border-[#e1e6e1] focus:border-[#57b26a] focus:ring-[#57b26a]"
                  }`}
              >
                <option value="">Select</option>
                {sweetsOptions.map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>

            <div className="mb-4">
              <label className="block text-[13px] text-[#24312d] mb-2">
                Which cooking oil is generally used at home? <span className="text-red-500">*</span>
              </label>
              <select
                name="cookingOil"
                value={dietaryData.cookingOil}
                onChange={(e) => {
                  handleInputChange("cookingOil", e.target.value);
                  setShowOtherCookingOil(e.target.value === "Other");
                }}
                className={`w-full h-[42px] border rounded-md px-4 text-sm outline-none focus:ring-1 ${formErrors.cookingOil
                    ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                    : "border-[#e1e6e1] focus:border-[#57b26a] focus:ring-[#57b26a]"
                  }`}
              >
                <option value="">Select</option>
                {cookingOilOptions.map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>

            {showOtherCookingOil && (
              <div className="mb-4">
                <input
                  type="text"
                  value={dietaryData.cookingOilOther}
                  onChange={(e) => handleInputChange("cookingOilOther", e.target.value)}
                  placeholder="Please specify other cooking oil"
                  className="w-full h-[42px] border border-[#e1e6e1] rounded-md px-4 text-sm outline-none focus:border-[#57b26a]"
                />
              </div>
            )}

            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <label className="text-[13px] text-[#24312d]">
                  Any packaged foods consumed daily?
                </label>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={dietaryData.packagedFoodsConsumption}
                    onChange={(e) => handleInputChange("packagedFoodsConsumption", e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#57b26a]"></div>
                  <span className="ml-3 text-sm text-[#24312d]">
                    {dietaryData.packagedFoodsConsumption ? "Yes" : "No"}
                  </span>
                </label>
              </div>
              {dietaryData.packagedFoodsConsumption && (
                <textarea
                  name="packagedFoodsDetails"
                  value={dietaryData.packagedFoodsDetails}
                  onChange={(e) => handleInputChange("packagedFoodsDetails", e.target.value)}
                  rows="2"
                  placeholder="What do you eat? (e.g., protein bar or biscuit)"
                  className={`w-full border rounded-md px-4 py-2 text-sm outline-none focus:ring-1 mt-2 ${formErrors.packagedFoodsDetails
                      ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                      : "border-[#e1e6e1] focus:border-[#57b26a] focus:ring-[#57b26a]"
                    }`}
                />
              )}
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-[13px] text-[#24312d]">
                  Weekend eating pattern different?
                </label>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={dietaryData.weekendEatingPattern}
                    onChange={(e) => handleInputChange("weekendEatingPattern", e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#57b26a]"></div>
                  <span className="ml-3 text-sm text-[#24312d]">
                    {dietaryData.weekendEatingPattern ? "Yes" : "No"}
                  </span>
                </label>
              </div>
              {dietaryData.weekendEatingPattern && (
                <textarea
                  name="weekendEatingDetails"
                  value={dietaryData.weekendEatingDetails}
                  onChange={(e) => handleInputChange("weekendEatingDetails", e.target.value)}
                  rows="2"
                  placeholder="How is it different? (e.g., dine out on Saturdays)"
                  className={`w-full border rounded-md px-4 py-2 text-sm outline-none focus:ring-1 mt-2 ${formErrors.weekendEatingDetails
                      ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                      : "border-[#e1e6e1] focus:border-[#57b26a] focus:ring-[#57b26a]"
                    }`}
                />
              )}
            </div>
          </div>

          <div className="bg-white border border-[#e8ece8] rounded-xl p-5">
            <h3 className="text-[15px] font-semibold text-[#24312d] mb-2">
              Save Preferences
            </h3>

            <p className="text-[12px] text-[#98a19b] leading-5 mb-5">
              Updating your dietary preferences will recalculate your upcoming
              meal recommendations and grocery lists.
            </p>

            <button
              onClick={handleSaveChanges}
              disabled={isSaving}
              className="w-full h-[42px] bg-[#57b26a] text-white rounded-lg text-sm flex items-center justify-center gap-2 mb-3 hover:bg-[#4a9e5c] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save size={15} />
                  Save Changes
                </>
              )}
            </button>

            <button
              onClick={handleDiscard}
              disabled={isSaving}
              className="w-full h-[42px] border border-[#dfe5df] rounded-lg text-sm text-[#57b26a] hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Discard
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DietaryPreferences;